import React from 'react';
import {t} from 'i18next';
import {Trans} from 'react-i18next';
import {Stack, Box, Typography, IconButton, Divider} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import './SearchModifiers.scss';
import ArrCheckbox from '../../common/arr-checkbox/ArrCheckbox';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {RoleId} from '../../../shared/constants';
import {SessionSearch} from '../types/SessionSearch';

export interface SearchModifierProps {
  userRoleId: number;
  item: SessionSearch;
  onChange?: (item: SessionSearch) => void;
}

const SearchModifiers: React.FC<SearchModifierProps> = ({
  userRoleId,
  item,
  onChange,
}) => {
  const handleRegexOnChange = () => {
    item.regexOnWorkProductAttributes = !item.regexOnWorkProductAttributes;
    onChange?.(item);
  };

  const handleFamilyOnChange = () => {
    item.familyMemberDocuments = !item.familyMemberDocuments;
    onChange?.(item);
  };

  const handleThreadOnChange = () => {
    item.threadedEmailDocuments = !item.threadedEmailDocuments;
    onChange?.(item);
  };

  const handleDuplicatesOnChange = () => {
    item.nearDuplicateDocuments = !item.nearDuplicateDocuments;
    onChange?.(item);
  };

  const renderRegexOptions = () => {
    // Render this option for [Project Admins] ONLY
    const userIsProjectAdmin =
      userRoleId === Number(RoleId.ProjectAdministrator);
    if (userIsProjectAdmin) {
      const labelWithTooltip = (
        <>
          {t('sessionSearch.modifiers.regexOnWorkProductAttributes')}
          &nbsp;
          <ArrTooltipWrapper
            trigger={
              <IconButton
                aria-label="Info"
                sx={{padding: 0}}
                className="icon-info"
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            }
          >
            <Trans
              i18nKey="sessionSearch.modifiers.regexOnWorkProductAttributesTooltip"
              components={{
                anchor: (
                  <a
                    href="https://aurora-review.consilio.com/"
                    target="_blank"
                    data-testid={`search-modifiers-a`}
                  />
                ),
              }}
            />
          </ArrTooltipWrapper>
        </>
      );

      return (
        <>
          <Box>
            <Divider className="modifiers-divider" />
          </Box>
          <Box className="modifiers-regex-item">
            <ArrCheckbox
              size="medium"
              label="regexOnWorkProductAttributes"
              labelText={labelWithTooltip}
              checked={item.regexOnWorkProductAttributes}
              onChange={handleRegexOnChange}
              aria-label="regexOnWorkProductAttributes-checkbox"
            />
          </Box>
        </>
      );
    }

    return <></>;
  };

  const renderModifiersCheckboxes = () => {
    return (
      <>
        <Box className="modifiers-title-item" sx={{display: 'flex'}}>
          <Typography
            className="typo-text subhead5_size modifiers-title"
            data-testid={`search-modifiers-title`}
            tabIndex={0}
          >
            {t('sessionSearch.modifiers.title')}
          </Typography>
          &nbsp;
          <ArrTooltipWrapper
            trigger={
              <IconButton
                aria-label="Info"
                sx={{padding: 0}}
                className="icon-info"
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            }
          >
            <Trans i18nKey="sessionSearch.modifiers.titleTooltip" />
          </ArrTooltipWrapper>
        </Box>
        <Box className="modifiers-family-item">
          <ArrCheckbox
            size="medium"
            label="familyMemberDocuments"
            labelText={t('sessionSearch.modifiers.familyMemberDocuments')}
            checked={item.familyMemberDocuments}
            onChange={handleFamilyOnChange}
            aria-label="familyMemberDocuments-cb"
          />
        </Box>
        <Box className="modifiers-thread-item">
          <ArrCheckbox
            size="medium"
            label="threadedEmailDocuments"
            labelText={t('sessionSearch.modifiers.threadedEmailDocuments')}
            checked={item.threadedEmailDocuments}
            onChange={handleThreadOnChange}
            aria-label="threadedEmailDocuments-cb"
          />
        </Box>
        <Box className="modifiers-duplicate-item">
          <ArrCheckbox
            size="medium"
            label="nearDuplicateDocuments"
            labelText={t('sessionSearch.modifiers.nearDuplicateDocuments')}
            checked={item.nearDuplicateDocuments}
            onChange={handleDuplicatesOnChange}
            aria-label="nearDuplicateDocument-cb"
          />
        </Box>
      </>
    );
  };

  return (
    <Stack spacing={1} className="session-search-modifiers">
      {renderModifiersCheckboxes()}
      {renderRegexOptions()}
    </Stack>
  );
};
export default SearchModifiers;
